import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CharterAdministratorDto } from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { CharterHistoryListComponent } from '@features/dashboard/company/corporate-charter/components/charter-history-list/charter-history-list.component';
import { CharterPersonDocumentComponent } from '@features/dashboard/company/corporate-charter/components/charter-person-document/charter-person-document.component';
import { CharterProvenanceComponent } from '@features/dashboard/company/corporate-charter/components/charter-provenance/charter-provenance.component';
import {
  CharterProvenance,
  administratorHistoryRows,
  isLaterThanBase,
  lastSeen,
} from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';
import { GOVERNANCE_BODY_LABEL } from '@features/dashboard/company/corporate-charter/corporate-charter.util';

/**
 * Um administrador, conselheiro ou diretor: nome, cargo, documento mascarado, mandato, aviso da Receita, de onde veio
 * ("vigente desde <ato>") e **Ver histórico** com o que cada ato dizia dele. Com `former`, é um ex-administrador: o
 * selo mostra o último ato que o listava.
 */
@Component({
  selector: 'app-charter-administrator-item',
  imports: [
    VaultDatePipe,
    CharterPersonDocumentComponent,
    CharterProvenanceComponent,
    CharterHistoryListComponent,
  ],
  templateUrl: './charter-administrator-item.component.html',
  styleUrl: './charter-administrator-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterAdministratorItemComponent {
  private readonly dates = inject(DateFormatterService);

  readonly admin = input.required<CharterAdministratorDto>();
  readonly isCorporation = input(false);
  /** Ex-administrador (não está no vigente). */
  readonly former = input(false);
  /** Mostra o órgão ao lado do cargo (lista de ex-administradores, que não agrupa por órgão). */
  readonly showBody = input(false);
  /** Ato da fonte do cartão: administrador nomeado em outro ato mostra "vigente desde" e o selo de alteração. */
  readonly base = input<CharterProvenance | null | undefined>(null);

  protected readonly changedFromBase = computed(
    () => !this.base() || isLaterThanBase(this.admin(), this.base())
  );

  /** "Conselho de administração · Presidente · sócio", sem ponto solto antes do primeiro item. */
  protected readonly roleLine = computed(() => {
    const a = this.admin();
    const parts = [
      this.bodyLabel(),
      a.role,
      a.isHolder && !this.isCorporation() ? 'sócio' : null,
    ].filter((part): part is string => !!part);
    return parts.length > 0 ? parts.join(' · ') : null;
  });

  protected readonly historyOpen = signal(false);

  protected readonly history = computed(() =>
    administratorHistoryRows(this.admin().history ?? [], value =>
      this.dates.formatDateInstant(value, true)
    )
  );

  protected readonly bodyLabel = computed(() => {
    const body = this.admin().body;
    return this.showBody() && body ? GOVERNANCE_BODY_LABEL[body] : null;
  });

  /** Ex-administrador: o último ato que o listava. */
  protected readonly lastAct = computed(() =>
    this.former() ? lastSeen(this.admin().history ?? []) : null
  );

  /** "até AGO 2027", "prazo indeterminado" ou a data do fim do mandato. */
  protected readonly term = computed(() => {
    const admin = this.admin();
    if (admin.termDescription) {
      return admin.termDescription;
    }
    return admin.termEnd ? `até ${this.dates.formatDateInstant(admin.termEnd, true)}` : null;
  });
}
